import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FileText, File, Loader2 } from "lucide-react";
import { useState, useEffect } from "react";

interface FeedbackFormatDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentId: string | null;
  exportFeedback: (
    documentId: string,
    format: "pdf" | "markdown"
  ) => Promise<void>;
}

interface FormatButtonProps {
  format: "pdf" | "markdown";
  label: string;
  description: string;
  icon: React.ReactNode;
  isSelected: boolean;
  onClick: (e: React.MouseEvent) => void;
}

const FormatButton = ({
  label,
  description,
  icon,
  isSelected,
  onClick,
}: FormatButtonProps) => {
  const handleClick = (e: React.MouseEvent) => {
    onClick(e);
  };

  return (
    <button
      onClick={handleClick}
      className={`flex flex-col items-center justify-center p-6 rounded-lg border-2 transition-all ${
        isSelected
          ? "border-brand bg-primary/5"
          : "border-muted hover:border-muted-foreground/30"
      }`}
    >
      <div className="w-12 h-12 mb-3 flex items-center justify-center">
        {icon}
      </div>
      <span className="font-medium text-center mb-1">{label}</span>
      <span className="text-xs text-muted-foreground text-center">
        {description}
      </span>
    </button>
  );
};

export const FeedbackFormatDialog = ({
  open,
  onOpenChange,
  documentId,
  exportFeedback,
}: FeedbackFormatDialogProps) => {
  const [selectedFormat, setSelectedFormat] = useState<
    "pdf" | "markdown" | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);

  // Reset selection when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedFormat(null);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!documentId || !selectedFormat) return;

    setIsLoading(true);
    try {
      await exportFeedback(documentId, selectedFormat);
      onOpenChange(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[500px]"
        onInteractOutside={(e) => e.preventDefault()}
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>Download feedback</DialogTitle>
          <DialogDescription>
            Choose the format for downloading the feedback.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 py-4">
          <FormatButton
            format="pdf"
            label="PDF"
            description="Formatted document"
            icon={<FileText className="h-8 w-8" />}
            isSelected={selectedFormat === "pdf"}
            onClick={() => setSelectedFormat("pdf")}
          />

          <FormatButton
            format="markdown"
            label="Markdown"
            description="Plain text format"
            icon={<File className="h-8 w-8" />}
            isSelected={selectedFormat === "markdown"}
            onClick={() => setSelectedFormat("markdown")}
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isLoading || !selectedFormat}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
